// Observable sync status, so the UI can tell the user what the background sync
// is doing (brief §2: "une information de synchronisation en cours"). Kept
// deliberately tiny — a single current value plus a listener set — because the
// only writer is the sync manager and the only readers are status indicators.

export type SyncStatus =
  | 'disabled' // no relay configured — nothing to sync
  | 'idle' // up to date, nothing running
  | 'syncing' // a fetch is in flight
  | 'offline' // the device reports no network
  | 'error'; // the last attempt failed; a retry is scheduled

export interface SyncState {
  status: SyncStatus;
  /** Epoch ms of the last successful sync, or null if none yet. */
  lastSyncAt: number | null;
  /** A profile edit is saved locally but hasn't reached the relay yet — queued for retry. */
  pendingPublish: boolean;
}

// Starts 'disabled' (indicator hidden) until the sync manager reports in, so a
// freshly loaded app doesn't flash a misleading "Synchronisation…".
let state: SyncState = { status: 'disabled', lastSyncAt: null, pendingPublish: false };
const listeners = new Set<(s: SyncState) => void>();

export function getSyncState(): SyncState {
  return state;
}

/** Merge a patch into the current state and notify listeners. */
export function setSyncState(patch: Partial<SyncState>): void {
  state = { ...state, ...patch };
  for (const l of [...listeners]) l(state);
}

/** Subscribe to state changes. Returns an unsubscribe function. */
export function onSyncState(fn: (s: SyncState) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
